'use client';

import { useEffect } from 'react';
import sendGoogleTagEvent from '@hooks/useSendGoogleTagEvent';

/**
 * @description Fires a GA4 sign_up event via dataLayer once if a short-lived cookie flag exists, then deletes the cookie.
 * Cookie may be encoded 0–2 times. Expected decoded shape: "1;method;type" (e.g., "1;email;business").
 */
export const useGaSignUpOnce = (cookieName = 'ga_signup') => {
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const raw = document.cookie.split('; ').find((row) => row.startsWith(`${cookieName}=`));
    if (!raw) return undefined;

    const encoded = raw.split('=')[1] || '';

    // Decode up to 3 times until literal semicolons appear, to tolerate double-encoding like "1%253Bemail%253Bbusiness"
    let decoded = encoded;
    for (let i = 0; i < 3; i += 1) {
      if (decoded.includes(';')) break; // already has literal semicolons
      if (/%[0-9A-Fa-f]{2}/.test(decoded)) {
        try {
          decoded = decodeURIComponent(decoded);
        } catch {
          break; // stop if decoding fails
        }
      } else {
        break; // nothing more to decode
      }
    }

    const parts = decoded.split(';'); // ["1", "email", "business"]
    const accountType = (parts[2] || 'personal').trim().toLowerCase();

    sendGoogleTagEvent('sign_up', 'Custom', {
      account_type: accountType,
    });

    // Delete cookie to avoid duplicate firing on refresh/back
    document.cookie = `${cookieName}=; Max-Age=0; path=/`;

    return undefined;
  }, [cookieName]);
};
